/** Contains the top bar used within the chat pages */

import { effect } from "@preact/signals";
import * as preact from "preact";

import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import * as tts from "tts";
import DropdownMenu from "views/DropdownMenu";
import SessionHeaderView from "views/SessionHeaderView";
import * as speakingModeControls from "views/speakingModeControls";
import StreakView from "views/StreakView";
import * as ttsMenu from "views/ttsMenu";

interface Props {
  progress?: number; // Number from 0 to 1
  language?: string;
  onClose: () => void;
  children: preact.ComponentChildren;
}

export class ChatContainerView extends preact.Component<Props> {
  disposeUserEffect?: () => void;
  disposeTtsAvailabilityEffect?: () => void;

  componentDidMount() {
    // Re-render when the server-side Azure voice pref changes, since
    // picking an Azure voice in the picker updates user.ttsVoices rather
    // than localStorage. Also subscribe to speakingMode so the menu's
    // toggle stays in sync when it's flipped from elsewhere (Settings tab).
    this.disposeUserEffect = effect(() => {
      void stores.user.state.value.ttsVoices;
      void stores.user.state.value.speakingMode;
      this.forceUpdate();
    });
    this.setupTtsAvailabilityEffect();
  }

  componentDidUpdate(previousProps: Props): void {
    if (previousProps.language !== this.props.language) {
      this.setupTtsAvailabilityEffect();
    }
  }

  componentWillUnmount(): void {
    this.disposeUserEffect?.();
    this.disposeTtsAvailabilityEffect?.();
  }

  private setupTtsAvailabilityEffect() {
    this.disposeTtsAvailabilityEffect?.();
    const language = this.props.language;
    if (!language) {
      this.disposeTtsAvailabilityEffect = undefined;
      return;
    }
    this.disposeTtsAvailabilityEffect = effect(() => {
      void tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language,
      }).value;
      this.forceUpdate();
    });
  }

  render() {
    return (
      <div class="chatColumn">
        <div class="headerContainer">
          <SessionHeaderView
            onClose={this.props.onClose}
            progress={this.props.progress}
            title="Conversations"
          >
            <StreakView store={stores.streak} hideUponCompletion={false} />
            <DropdownMenu
              items={[
                {
                  type: "toggle",
                  label: "Auto-Pronounce",
                  initialValue: Boolean(stores.user.state.peek().speakingMode),
                  disabled: this.props.language
                    ? tts.getAvailabilitySignal({
                        user: stores.user,
                        feature: "speakingMode",
                        language: this.props.language,
                      }).value === "unsupported"
                    : false,
                  onDisabledClick: () => {
                    const language = this.props.language ?? "";
                    const languageName =
                      languageMap.map[language]?.english ?? language;
                    alert(
                      `No TTS voices are currently available for ${languageName}`
                    );
                  },
                  onToggle: (value) => {
                    speakingModeControls.setSpeakingModeWithVoicePrompt(
                      value,
                      this.props.language
                    );
                  },
                },
                ...ttsMenu.buildVoiceMenuItems({
                  language: this.props.language,
                  feature: "speakingMode",
                  onChange: () => this.forceUpdate(),
                }),
              ]}
            />
          </SessionHeaderView>
        </div>
        {this.props.children}
      </div>
    );
  }
}

export default ChatContainerView;
